import { Group, PasswordInput, Popover, Progress, Stack, Text } from '@mantine/core';
import { BsCheck2, BsXCircle } from 'react-icons/bs';
import { PasswordRequirement, useGetPasswordPolicy } from 'hooks/api/user/useGetPasswordPolicy';
import { useEffect } from 'react';

interface PasswordFieldInputProps {
  label: string;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  onProgressChanged?: (progress: number) => void;
}

const calculateProgress = (requirements: PasswordRequirement[], password?: string) => {
  if (!password) return 0;
  const passedRequirements = requirements.filter((requirement) => requirement.re.test(password));
  return (passedRequirements.length / requirements.length) * 100;
};

const showProgressColor = (progress: number) => {
  if (progress >= 100) return 'var(--mantine-color-turquoise-5)';
  if (progress > 50) return 'var(--mantine-color-peach-7)';
  return 'var(--mantine-color-red-5)';
};

export const PasswordField = ({
  label,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  error,
  onProgressChanged,
}: PasswordFieldInputProps) => {
  const { requirements, policy } = useGetPasswordPolicy();
  const progress = calculateProgress(requirements, value);
  useEffect(() => {
    onProgressChanged?.(progress);
  }, [progress, onProgressChanged]);

  return (
    <Popover width={'target'} position={'top'} withArrow shadow={'md'}>
      <Popover.Target>
        <PasswordInput
          label={label}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          error={error}
          withAsterisk
          maxLength={policy?.maximumLength}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Stack>
          <Progress value={progress} color={showProgressColor(progress)} />
          {requirements?.map((requirement, index) => {
            const done = requirement.re.test(value ?? '');
            const color = done ? 'var(--mantine-color-turquoise-5)' : 'var(--mantine-color-red-5)';
            return (
              <Group key={index}>
                {done ? <BsCheck2 color={color} /> : <BsXCircle color={color} />}
                <Text size={'sm'} c={color}>
                  {requirement.label}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
